import { useEffect, useRef, useState } from "react";
import api, { apiError, brl, fileUrl, printAgent } from "@/lib/api";
import { toast } from "sonner";
import { FileCheck, FileDown, FileX2, FileEdit, FileText, ReceiptText, Printer, Plus, Trash2, RefreshCw, FileArchive } from "lucide-react";
import { ConfirmDialog, Modal } from "@/components/panel/ui";
import Pagination from "@/components/panel/Pagination";
import usePaginatedList from "@/hooks/usePaginatedList";
import EmitInvoiceModal from "./fiscal/EmitInvoiceModal";
import ExportXmlsModal from "./fiscal/ExportXmlsModal";
import {
  Badge, Button, DataTable, Field, PageHeader, Textarea, FOCUS_RING,
} from "@/components/ui-kit";
import { cn } from "@/lib/utils";

/**
 * Manda a nota para a impressora pelo agente local — cada tipo no SEU papel.
 *
 * A NFC-e vai pelo cupom que o backend remonta na medida da bobina
 * (`/cupom`), e não pelo DANFE do provedor: aquele é uma página feita para a
 * TELA, e na térmica de 80mm o lado direito saía fora do papel, levando junto o
 * valor de cada item e o fim da chave de acesso.
 *
 * A NF-e continua no PDF oficial: ela é documento de folha inteira, e espremê-la
 * em bobina seria o erro simétrico.
 */
async function printDanfe(nota) {
  const cupom = nota.type === "nfce";
  const tid = toast.loading(cupom ? "Enviando cupom para a impressora..." : "Enviando DANFE para a impressora...");
  try {
    await printAgent.printApiDoc(`/invoices/${nota.id}/${cupom ? "cupom" : "pdf"}`);
    toast.success(cupom ? "Cupom enviado para impressão" : "DANFE enviada para impressão", { id: tid });
  } catch (e) {
    toast.error(e.message || "Falha ao imprimir", { id: tid });
  }
}

/* Tom semântico por status fiscal (era um mapa de classes cruas).
   Verde só para autorizada; âmbar ("warning") para o que ainda está em
   andamento — de propósito NÃO é "progress"/teal, porque nota pendente segura a
   entrega do pedido e precisa de acompanhamento, não é um "tudo certo";
   vermelho apenas para falha/cancelamento.
   `erro_autorizacao` e `denegada` estavam fora do mapa antigo e caíam no cinza
   neutro, apesar de serem recusas da SEFAZ — agora aparecem como falha. */
const INV_STATUS_TONE = {
  autorizada: "success",
  processando: "warning",
  enviada: "warning",
  pendente: "warning",
  cancelada: "danger",
  rejeitada: "danger",
  denegada: "danger",
  erro_autorizacao: "danger",
  erro: "danger",
};
const invStatusTone = (s) => INV_STATUS_TONE[s] || "neutral";

// O status vem cru do backend; "erro_autorizacao" não é texto de tela.
const INV_STATUS_LABEL = {
  autorizada: "Autorizada",
  processando: "Processando",
  enviada: "Enviada",
  pendente: "Pendente",
  cancelada: "Cancelada",
  rejeitada: "Rejeitada",
  denegada: "Denegada",
  erro_autorizacao: "Erro na autorização",
  erro: "Erro",
};

const FAILED_STATUS = ["erro_autorizacao", "rejeitada", "denegada", "erro"];

const KIND_META = {
  nfe: {
    icon: FileText,
    label: "NF-e",
    caption: "Notas fiscais eletrônicas emitidas",
    emptyTitle: "Nenhuma NF-e emitida",
    emptyDesc: "Emita pela fila de pedidos sem nota acima, ou avulsa, informando cliente e itens.",
    emptyTestId: "fiscal-nfe-empty",
  },
  nfce: {
    icon: ReceiptText,
    label: "NFC-e",
    caption: "Cupons fiscais eletrônicos emitidos",
    emptyTitle: "Nenhum cupom fiscal emitido",
    emptyDesc: "Os cupons aparecem aqui automaticamente a cada venda concluída no PDV (balcão).",
    emptyTestId: "fiscal-nfce-empty",
  },
};

/* Ação só com ícone que precisa ser <a> (abre PDF/XML em outra aba): o Button do
   kit renderiza <button>, então aqui a classe é escrita à mão com a mesma
   altura do `size="iconSm"`. */
const ICON_LINK = "inline-flex items-center justify-center h-8 w-8 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-brand-600 transition-colors";

function InvoiceTable({ list, kind, onCancel, onCorrect, onDelete, onReemit, reemitting, onRefresh, refreshing, onEmit }) {
  const meta = KIND_META[kind];

  const columns = [
    {
      key: "numero", header: "Nº", mono: true, sortable: true, className: "font-semibold text-slate-900",
      cell: (i) => i.number,
    },
    { key: "origem", header: "Origem", mono: true, className: "text-slate-500", cell: (i) => i.source_number || "—" },
    { key: "cliente", header: "Cliente", sortable: true, className: "text-slate-600", cell: (i) => i.customer_name || "Consumidor Final" },
    { key: "total", header: "Total", align: "right", mono: true, sortable: true, sortDir: "desc", cell: (i) => brl(i.total) },
    {
      key: "status", header: "Status", sortable: true,
      cell: (i) => (
        <>
          <span className="inline-flex items-center gap-1.5 flex-wrap">
            <Badge tone={invStatusTone(i.status)}>{INV_STATUS_LABEL[i.status] || i.status}</Badge>
            {i.correction_letters?.length > 0 && (
              <span className="text-[10px] font-semibold text-brand-600">CC-e ({i.correction_letters.length})</span>
            )}
          </span>
          {FAILED_STATUS.includes(i.status) && i.status_message && (
            <p className="text-[11px] text-red-600 mt-1 max-w-xs" data-testid="fiscal-error-reason" title={i.status_message}>
              {i.status_message}
            </p>
          )}
        </>
      ),
    },
    {
      key: "acoes", header: "Ações", align: "right", className: "whitespace-nowrap",
      cell: (i) => (
        <div className="inline-flex items-center justify-end gap-1">
          {i.status === "processando" ? (
            // NFC-e/NF-e autorizam de forma assíncrona; consulta o status atual.
            <Button
              variant="ghost" size="sm" icon={RefreshCw} loading={refreshing === i.id}
              onClick={() => onRefresh(i)} data-testid="fiscal-refresh-btn"
              title="Consultar status" aria-label={`Consultar status da nota ${i.number}`}
            >
              Consultar
            </Button>
          ) : FAILED_STATUS.includes(i.status) ? (
            // Tentativa não autorizada: não há PDF/XML; reemitir ou descartar.
            <>
              <Button
                variant="ghost" size="iconSm" icon={RefreshCw} loading={reemitting === i.id}
                onClick={() => onReemit(i)} data-testid="fiscal-reemit-btn"
                title="Reemitir" aria-label={`Reemitir nota ${i.number}`} className="hover:text-brand-600"
              />
              <Button
                variant="ghost" size="iconSm" icon={Trash2} onClick={() => onDelete(i)}
                data-testid="fiscal-delete-btn" title="Excluir tentativa"
                aria-label={`Excluir tentativa ${i.number}`} className="hover:text-red-600"
              />
            </>
          ) : (
            <>
              <a
                href={fileUrl(`/invoices/${i.id}/pdf`)} target="_blank" rel="noreferrer"
                data-testid="fiscal-pdf-btn" title="Abrir DANFE PDF"
                aria-label={`Abrir DANFE da nota ${i.number}`} className={ICON_LINK}
              >
                <FileDown size={15} aria-hidden />
              </a>
              <Button
                variant="ghost" size="iconSm" icon={Printer} onClick={() => printDanfe(i)}
                data-testid="fiscal-print-btn"
                title={i.type === "nfce" ? "Imprimir cupom na bobina" : "Imprimir DANFE em folha"}
                aria-label={`Imprimir nota ${i.number}`} className="hover:text-brand-600"
              />
              {/* Sem agente instalado (ou impressora fora do ar), o cupom abre no
                  navegador e sai pelo Ctrl+P — é a saída que impede o balcão de
                  ficar sem documento por causa de software. */}
              {i.type === "nfce" && (
                <a
                  href={fileUrl(`/invoices/${i.id}/cupom`)} target="_blank" rel="noreferrer"
                  data-testid="fiscal-cupom-btn" title="Abrir cupom (bobina)"
                  aria-label={`Abrir cupom da nota ${i.number}`} className={ICON_LINK}
                >
                  <ReceiptText size={15} aria-hidden />
                </a>
              )}
              <a
                href={fileUrl(`/invoices/${i.id}/xml`)} target="_blank" rel="noreferrer"
                data-testid="fiscal-xml-btn" title="XML"
                aria-label={`Baixar XML da nota ${i.number}`} className={cn(ICON_LINK, "text-xs font-mono w-auto px-2")}
              >
                XML
              </a>
              {i.status === "autorizada" && (
                <>
                  <Button
                    variant="ghost" size="iconSm" icon={FileEdit} onClick={() => onCorrect(i)}
                    data-testid="fiscal-correction-btn" title="Carta de correção"
                    aria-label={`Carta de correção da nota ${i.number}`} className="hover:text-brand-600"
                  />
                  <Button
                    variant="ghost" size="iconSm" icon={FileX2} onClick={() => onCancel(i)}
                    data-testid="fiscal-cancel-btn" title="Cancelar nota"
                    aria-label={`Cancelar nota ${i.number}`} className="hover:text-red-600"
                  />
                </>
              )}
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <DataTable
      rowTestId="fiscal-invoice-row"
      caption={meta.caption}
      columns={columns}
      rows={list.items}
      sort={list.sort}
      onSortChange={list.setSort}
      rowKey={(i) => i.id}
      loading={list.isLoading}
      error={list.isError}
      onRetry={list.refetch}
      empty={{
        icon: meta.icon,
        title: meta.emptyTitle,
        desc: meta.emptyDesc,
        // Sem filtro nesta tela, a saída do vazio é criar o primeiro registro.
        action: <Button icon={Plus} onClick={onEmit}>Emitir {meta.label}</Button>,
        testId: meta.emptyTestId,
      }}
    />
  );
}

export default function Fiscal() {
  const [tab, setTab] = useState("nfe"); // nfe | nfce
  const [correcting, setCorrecting] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [emitting, setEmitting] = useState(null);
  const [reemitting, setReemitting] = useState(null);
  const [refreshing, setRefreshing] = useState(null);
  const polled = useRef(new Set());
  const [ccBusy, setCcBusy] = useState(false);
  const [text, setText] = useState("");
  const [summary, setSummary] = useState(null);
  const [emitModal, setEmitModal] = useState(null); // 'nfe' | 'nfce'
  const [exportOpen, setExportOpen] = useState(false);

  // Cada aba é uma consulta própria (type no servidor) em vez de um filtro
  // sobre uma lista única carregada inteira.
  const nfeList = usePaginatedList("/admin/invoices", { type: "nfe" });
  const nfceList = usePaginatedList("/admin/invoices", { type: "nfce" });
  // Pedidos sem NF-e: o cruzamento é feito no banco (pending_invoice).
  const pendingList = usePaginatedList("/admin/orders", { pending_invoice: 1 }, { perPage: 10 });

  const loadSummary = () => api.get("/admin/invoices/summary").then((r) => setSummary(r.data)).catch(() => {});
  const load = () => { nfeList.refetch(); nfceList.refetch(); pendingList.refetch(); loadSummary(); };
  useEffect(() => { loadSummary(); }, []);

  // Notas assíncronas: em produção o webhook atualiza; aqui consultamos cada nota
  // em "processando" uma vez ao aparecer, para o status não ficar preso na tela.
  useEffect(() => {
    const pend = [...nfeList.items, ...nfceList.items].filter((i) => i.status === "processando" && !polled.current.has(i.id));
    if (!pend.length) return;
    pend.forEach((i) => polled.current.add(i.id));
    Promise.all(pend.map((i) => api.post(`/admin/invoices/${i.id}/refresh-status`).catch(() => {})))
      .then((res) => { if (res.some((r) => r?.data && r.data.status !== "processando")) load(); });
  }, [nfeList.items, nfceList.items]); // eslint-disable-line react-hooks/exhaustive-deps

  const emit = async (o) => {
    if (emitting) return;               // trava anti-duplo-envio à SEFAZ
    setEmitting(o.id);
    try {
      const { data } = await api.post("/admin/invoices/emit", { order_id: o.id });
      // A SEFAZ pode recusar já na resposta — não diga "emitida" nesse caso.
      if (FAILED_STATUS.includes(data.status)) {
        toast.error(data.status_message || "A SEFAZ recusou a emissão.", { duration: 8000 });
      } else {
        toast.success(`NF-e do pedido ${o.number} enviada para autorização`);
      }
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setEmitting(null); }
  };
  const cancel = async (inv) => {
    try { await api.post(`/admin/invoices/${inv.id}/cancel`); toast.success(`Nota ${inv.number} cancelada`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (inv) => {
    try { await api.delete(`/admin/invoices/${inv.id}`); toast.success("Tentativa excluída"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const refreshStatus = async (inv) => {
    if (refreshing) return;
    setRefreshing(inv.id);
    try {
      const { data } = await api.post(`/admin/invoices/${inv.id}/refresh-status`);
      if (data.status === "autorizada") toast.success(`Nota nº ${data.number} autorizada`);
      else if (FAILED_STATUS.includes(data.status)) toast.error(data.status_message || "A SEFAZ recusou a nota.", { duration: 8000 });
      // ainda processando: sem toast, o usuário tenta de novo em instantes
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRefreshing(null); }
  };
  const reemit = async (inv) => {
    if (reemitting) return;
    setReemitting(inv.id);
    try {
      const { data } = await api.post(`/admin/invoices/${inv.id}/reemit`);
      // A reemissão pode falhar de novo (ex.: dado ainda não corrigido).
      if (FAILED_STATUS.includes(data.status)) {
        toast.error(data.status_message || "A SEFAZ recusou de novo.", { duration: 8000 });
      } else {
        toast.success(`Reemitida — nº ${data.number} enviada para autorização`);
      }
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setReemitting(null); }
  };

  const sendCorrection = async () => {
    if (ccBusy) return;
    setCcBusy(true);
    try { await api.post(`/admin/invoices/${correcting.id}/correction`, { text }); toast.success("Carta de correção registrada"); setCorrecting(null); setText(""); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setCcBusy(false); }
  };

  /* Fila de pedidos pagos ainda sem NF-e. Tem paginação e ação por linha, então
     segue o formato das outras listagens (título fora da superfície, que quem
     desenha é o DataTable) — como tabela solta dentro de um Card ela ficava sem
     cabeçalho de coluna e sem estado de carregando/erro, e anunciava "todos os
     pedidos possuem nota" enquanto a primeira página ainda estava vindo. */
  const pendingColumns = [
    {
      key: "pedido", header: "Pedido", mono: true, className: "text-slate-900",
      cell: (o) => o.number,
    },
    { key: "cliente", header: "Cliente", className: "text-slate-600", cell: (o) => o.customer_name || "—" },
    { key: "total", header: "Total", align: "right", mono: true, cell: (o) => brl(o.total) },
    {
      key: "acoes", header: "Ações", align: "right", className: "whitespace-nowrap",
      cell: (o) => (
        <Button size="sm" loading={emitting === o.id} onClick={() => emit(o)} data-testid="fiscal-emit-btn">
          {emitting === o.id ? "Emitindo..." : "Emitir NF-e"}
        </Button>
      ),
    },
  ];

  const tabBtn = (t) => cn(
    "inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold transition-colors",
    FOCUS_RING,
    tab === t ? "bg-brand-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100",
  );

  const mes = summary?.[tab] ?? { count: 0, sum: 0 };

  /* NF-e e NFC-e são duas listagens distintas, não um filtro da mesma lista —
     por isso ficam como abas visíveis sempre, e não dentro de um FilterSheet.
     O total do mês anda junto porque também muda de aba para aba; ele NÃO é
     ação, então fica fora de `actions` — lá o PageHeader estica cada filho no
     celular e o parágrafo dividiria a linha com o botão de emitir. */
  const abas = (
    <div className="w-full flex flex-wrap items-center justify-between gap-3">
      <div className="flex gap-1.5 bg-white border border-slate-200 rounded-xl p-1.5 w-fit">
        <button type="button" onClick={() => setTab("nfe")} data-testid="fiscal-tab-nfe"
          aria-pressed={tab === "nfe"} data-ring="" className={tabBtn("nfe")}>
          <FileText size={16} aria-hidden /> Notas Fiscais (NF-e)
          <span className="text-xs opacity-70 font-mono">{nfeList.total}</span>
        </button>
        <button type="button" onClick={() => setTab("nfce")} data-testid="fiscal-tab-nfce"
          aria-pressed={tab === "nfce"} data-ring="" className={tabBtn("nfce")}>
          <ReceiptText size={16} aria-hidden /> Cupons Fiscais (NFC-e)
          <span className="text-xs opacity-70 font-mono">{nfceList.total}</span>
        </button>
      </div>
      <p className="text-sm text-slate-500" data-testid="fiscal-month-total">
        No mês: <b className="text-slate-800">{mes.count}</b> autorizada{mes.count === 1 ? "" : "s"} · <b className="font-mono tabular-nums text-slate-800">{brl(mes.sum)}</b>
      </p>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="admin-fiscal-page">
      <PageHeader
        eyebrow="Financeiro"
        title="Fiscal"
        icon={FileText}
        description="Emite e acompanha NF-e e NFC-e pela FocusNFe (integração simulada). Pedido sem nota autorizada não sai para entrega, e tentativa recusada pela SEFAZ precisa ser corrigida e reemitida."
        actions={(
          <>
            {/* O pacote do contador fica ao lado de "Emitir": é a outra coisa
                que se faz nesta tela, e antes só existia baixando XML nota a
                nota. Secundário porque é mensal, não diário. */}
            <Button variant="secondary" icon={FileArchive} onClick={() => setExportOpen(true)}
              data-testid="fiscal-export-btn">
              XMLs para o contador
            </Button>
            <Button icon={Plus} onClick={() => setEmitModal(tab)} data-testid="fiscal-emit-manual-btn">
              Emitir {tab === "nfe" ? "NF-e" : "NFC-e"}
            </Button>
          </>
        )}
        filters={abas}
      />

      {tab === "nfe" && (
        <>
          <section className="space-y-3">
            <h2 className="font-heading text-sm font-semibold text-slate-900">
              Pedidos online sem NF-e ({pendingList.total})
            </h2>
            <DataTable
              caption="Pedidos online ainda sem nota fiscal emitida"
              rowTestId="fiscal-pending-row"
              columns={pendingColumns}
              rows={pendingList.items}
              rowKey={(o) => o.id}
              loading={pendingList.isLoading}
              error={pendingList.isError}
              onRetry={pendingList.refetch}
              empty={{
                icon: FileCheck,
                title: "Todos os pedidos possuem nota",
                desc: "Nenhum pedido online pago está esperando NF-e. Um pedido novo aparece aqui assim que o pagamento é confirmado.",
                action: <Button variant="secondary" icon={Plus} onClick={() => setEmitModal("nfe")}>Emitir NF-e avulsa</Button>,
                testId: "fiscal-pending-empty",
              }}
            />
            <Pagination page={pendingList.page} lastPage={pendingList.lastPage} total={pendingList.total}
              perPage={10} onChange={pendingList.setPage} label="pedidos" />
          </section>

          {/* Com duas tabelas na mesma aba, a de baixo precisa dizer o que é. */}
          <section className="space-y-3">
            <h2 className="font-heading text-sm font-semibold text-slate-900">Notas emitidas</h2>
            <InvoiceTable list={nfeList} kind="nfe" onCancel={setCancelling} onCorrect={setCorrecting}
              onDelete={setDeleting} onReemit={reemit} reemitting={reemitting} onRefresh={refreshStatus}
              refreshing={refreshing} onEmit={() => setEmitModal("nfe")} />
            <Pagination page={nfeList.page} lastPage={nfeList.lastPage} total={nfeList.total} onChange={nfeList.setPage} label="notas" />
          </section>
        </>
      )}

      {tab === "nfce" && (
        <>
          <p className="text-sm text-slate-500">Cupons fiscais são emitidos automaticamente a cada venda concluída no PDV (balcão).</p>
          <InvoiceTable list={nfceList} kind="nfce" onCancel={setCancelling} onCorrect={setCorrecting}
            onDelete={setDeleting} onReemit={reemit} reemitting={reemitting} onRefresh={refreshStatus}
            refreshing={refreshing} onEmit={() => setEmitModal("nfce")} />
          <Pagination page={nfceList.page} lastPage={nfceList.lastPage} total={nfceList.total} onChange={nfceList.setPage} label="cupons" />
        </>
      )}

      <ConfirmDialog
        open={!!cancelling}
        onClose={() => setCancelling(null)}
        onConfirm={() => cancel(cancelling)}
        title="Cancelar nota fiscal"
        message={cancelling ? `Cancelar a ${cancelling.type === "nfe" ? "NF-e" : "NFC-e"} nº ${cancelling.number} (${brl(cancelling.total)}) junto à SEFAZ? Esta ação não pode ser desfeita.` : ""}
        confirmLabel="Cancelar nota"
      />

      <EmitInvoiceModal kind={emitModal || "nfe"} open={!!emitModal}
        onClose={() => setEmitModal(null)} onEmitted={load} />

      <ExportXmlsModal open={exportOpen} onClose={() => setExportOpen(false)} />

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => { remove(deleting); setDeleting(null); }}
        title="Excluir tentativa"
        message={deleting ? `Excluir a tentativa nº ${deleting.number}? Ela não foi autorizada pela SEFAZ, então não tem valor fiscal. Você poderá emitir de novo depois de corrigir o problema.` : ""}
        confirmLabel="Excluir"
      />

      <Modal open={!!correcting} onClose={() => { setCorrecting(null); setText(""); }}
        title={correcting ? `Carta de correção — Nota ${correcting.number}` : ""} testId="correction-dialog">
        <Field label="Descrição da correção" hint="O texto vai para a SEFAZ e fica anexado à nota — não serve para corrigir valores nem o destinatário.">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder="Descreva a correção..." data-testid="correction-text" />
        </Field>
        <Button onClick={sendCorrection} disabled={!text} loading={ccBusy} size="lg" block className="mt-3" data-testid="correction-submit">
          {ccBusy ? "Registrando..." : "Registrar CC-e"}
        </Button>
      </Modal>
    </div>
  );
}
